// Full EN + KN translations for Omkar Dairy Farm.
// Numbers, units, and currency (₹, L/day, %, kg, sqft) are kept identical across languages per product spec.

export const translations = {
    en: {
        common: {
            langName: "English",
            langShort: "EN",
            switchTo: "ಕನ್ನಡ",
            nav: {
                home: "Home",
                breeds: "Breeds",
                knowledge: "Knowledge",
                gallery: "Gallery",
                quiz: "Quiz",
                contact: "Contact",
                menu: "Menu",
                close: "Close",
            },
            buttons: {
                explore: "Explore",
                takeQuiz: "Take the 60s Breed Quiz",
                exploreBreeds: "Explore Breeds",
                learnDairy: "Learn Dairy Farming",
                readEssay: "Read essay",
                viewAll: "View all breeds",
                sending: "Sending…",
                sendEnquiry: "Send Enquiry",
                begin: "Begin the quiz",
                takeQuizShort: "Take the quiz",
                retake: "Retake quiz",
                previous: "Previous",
                next: "Next",
                openMaps: "Open in Google Maps →",
                notSureCta: "Not sure which one? Take the 60s quiz →",
                readStory: "Read the story",
                exploreAll: "Explore all breeds",
                sendLetter: "Send me the letter",
            },
            labels: {
                scroll: "Scroll",
                match: "Match",
                matchScore: "Match score",
                question: "Question",
                allBreeds: "All breeds",
                featured: "Featured",
                nativeTo: "Native to",
                readTime: "min read",
            },
        },
        floatingQuiz: {
            label: "60s Breed Quiz",
            aria: "Take the breed quiz",
        },
        home: {
            heroOverline: "Karnataka · Est. 2016 · Educational Platform",
            heroBrand: "Omkar\nDairy Farm",
            heroTagline: "Learn before you invest.",
            heroSub: "Helping future dairy farmers understand the true investment, management and economics of dairy farming — before purchasing cattle.",
            about: {
                label: "About Omkar",
                heading: "Every dairy cow is a\nlong-term investment.",
                p1: "Choosing the right breed is not about finding the biggest cow or the highest yielder. It is about matching an animal to your climate, your labour, your feed availability and your market.",
                p2: "Our mission is simple: to educate farmers before they invest — with clear, transparent, verifiable information about every breed suited to Karnataka.",
                stat1: "Breeds Documented",
                stat2: "Learning Topics",
                stat3: "Cattle for Sale",
            },
            breedsSection: {
                label: "Our Cow Breeds",
                heading: "Six breeds. Six\ndifferent economies.",
            },
            why: {
                label: "Why Breed Selection Matters",
                heading: "The wrong breed\ncan quietly bankrupt\na farm.",
                body: "Two farms with identical infrastructure can have wildly different economics — because of a single decision made on purchase day.",
                items: [
                    "Milk production", "Milk fat percentage", "Feeding requirements", "Feeding costs",
                    "Labour requirements", "Climate adaptability", "Disease resistance", "Operational costs", "Profitability",
                ],
            },
            learn: {
                label: "What You Will Learn",
                heading: "A complete curriculum,\nbefore you ever buy.",
            },
            quizCta: {
                label: "60-second breed quiz",
                heading1: "Which cow",
                heading2: "suits",
                heading3: "you?",
                body: "Five honest questions about your region, market, budget, labour and experience. We rank every breed against your reality — with the reasoning shown.",
                questionsLabel: "Questions",
            },
            featured: {
                label: "Featured Story",
            },
            begin: {
                label: "Begin the Journey",
                heading1: "Ready to start your",
                heading2: "dairy farming",
                heading3: "journey?",
                body: "No sales pitches. No pressure. Just clear, honest information — so you invest with confidence.",
            },
        },
        learningTopics: [
            { title: "Daily Milk Production", body: "Understand realistic per-cow yields across breeds and seasons." },
            { title: "Milk Fat Percentage", body: "Why fat and SNF drive pricing more than volume." },
            { title: "Purchase Cost", body: "Calf, heifer, first-calving and lactating price ranges." },
            { title: "Feed Requirements", body: "Green fodder, dry fodder, concentrate, minerals and water." },
            { title: "Feed Cost", body: "Daily, monthly and yearly feeding cost per animal." },
            { title: "Labour Requirement", body: "Man-hours required per cow per day for a healthy operation." },
            { title: "Housing", body: "Covered and open area, ventilation, flooring and drainage." },
            { title: "Health", body: "Disease resistance, heat stress and veterinary care." },
            { title: "Breeding", body: "First calving age, gestation, calving interval and AI." },
            { title: "Profitability", body: "How to calculate honest ROI before you invest." },
            { title: "ROI Planning", body: "Break-even months and long-term cash flow modeling." },
        ],
        breedsPage: {
            label: "Cow Breeds",
            heading: "Six breeds\ndocumented in depth.",
            intro: "A studied, data-driven look at the breeds most relevant to Karnataka — from the high-volume Holstein Friesian to the native Hallikar. Click any breed for its full economics.",
            noteLabel: "A note on the numbers",
            noteBody: "The figures on every breed profile are conservative Karnataka averages drawn from commonly accepted dairy management references. Actual results vary with feed quality, genetics, climate and management. Every value shown here can be replaced with your own verified farm data.",
        },
        breedProfile: {
            sections: {
                origin: "Origin",
                production: "Production Performance",
                economics: "Economics",
                economicsSub: "Approximate purchase cost by life stage",
                feeding: "Feeding",
                feedingSub: "Under typical Karnataka conditions",
                feedCost: "Daily Feeding Cost",
                feedCostSub: "Reference pricing — actual costs vary by region and season",
                labour: "Labour Requirement",
                housing: "Housing",
                health: "Health",
                reproduction: "Reproduction",
                environment: "Environmental Suitability",
                business: "Business Suitability",
                ratings: "Expert Ratings",
                ratingsSub: "Relative scores on a 0-100 scale",
                recommendation: "Expert Recommendation",
                gallery: "Gallery",
            },
            rows: {
                avgYield: "Average Milk Yield",
                peakYield: "Peak Milk Yield",
                lactationPeriod: "Lactation Period",
                milkPerLactation: "Milk per Lactation",
                milkFat: "Milk Fat",
                snf: "SNF",
                protein: "Protein",
                suitableFor: "Suitable for",
                calf: "Calf",
                heifer: "Heifer",
                firstCalving: "First Calving",
                lactatingCow: "Lactating Cow",
                pregnantCow: "Pregnant Cow",
                greenFodder: "Green Fodder",
                dryFodder: "Dry Fodder",
                concentrate: "Concentrate Feed",
                mineral: "Mineral Mixture",
                salt: "Salt",
                water: "Water",
                feedType: "Feed Type",
                quantity: "Quantity",
                daily: "Daily (₹)",
                monthly: "Monthly (₹)",
                yearly: "Yearly (₹)",
                totalDaily: "Total Daily Feed Cost",
                totalLabour: "Total labour",
                labourNote: "Man-hours required per cow per day for a healthy operation.",
                coveredArea: "Covered Area",
                openArea: "Open Area",
                ventilation: "Ventilation",
                floor: "Floor Type",
                drainage: "Drainage",
                bedding: "Bedding",
                diseaseResistance: "Disease Resistance",
                heatStress: "Heat Stress",
                tickResistance: "Tick Resistance",
                mastitis: "Mastitis Susceptibility",
                veterinary: "Veterinary Care",
                commonDiseases: "Common diseases",
                firstCalvingAge: "Age at First Calving",
                gestation: "Gestation Period",
                calvingInterval: "Calving Interval",
                fertility: "Fertility",
                insemination: "Artificial Insemination",
                natural: "Natural Breeding",
                heat: "Heat",
                rain: "Rain",
                cold: "Cold",
                humidity: "Humidity",
                bestRegions: "Best regions in Karnataka",
                bestSuited: "Best suited for",
                notRecommended: "Not recommended for",
                monthlyOp: "Estimated Monthly Operating Cost",
                monthlyMilk: "Expected Monthly Milk Production",
                expectedROI: "Expected ROI",
                advantages: "Advantages",
                challenges: "Challenges",
                coat: "Coat",
                classification: "Classification",
            },
            ratingLabels: {
                milkYield: "Milk Yield",
                milkQuality: "Milk Quality",
                feedEfficiency: "Feed Efficiency",
                maintenanceCost: "Maintenance Cost",
                diseaseResistance: "Disease Resistance",
                heatTolerance: "Heat Tolerance",
                labourRequirement: "Labour Requirement",
                profitability: "Profitability",
                beginnerFriendly: "Beginner Friendly",
            },
            tasks: {
                Feeding: "Feeding",
                Cleaning: "Cleaning",
                Milking: "Milking",
                Grooming: "Grooming",
                "Health Inspection": "Health Inspection",
                "Record Keeping": "Record Keeping",
            },
        },
        quiz: {
            label: "Breed Quiz",
            introTitle: "Which cow suits you?",
            introSub: "Five questions. Sixty seconds. A breed matched to your climate, market, budget and experience — with honest reasoning.",
            cardsQuestions: "Region, goal, budget, labour, experience.",
            cardsTime: "Take it faster than reading one page of an article.",
            cardsScore: "A ranked score across every breed we document.",
            timeLabel: "Time",
            questionsLabel: "Questions",
            scoreLabel: "Breed Match Score",
            resultLabel: "Your Match",
            recommended: "Recommended for you",
            whySuits: "Why this breed suits you",
            avgYield: "Avg. Yield",
            milkFat: "Milk Fat",
            monthlyOp: "Monthly Op. Cost",
            monthlyMilk: "Monthly Milk",
            seeProfile: "See full",
            profileSuffix: "profile",
            alsoConsider: "Also worth considering",
            followJourney: "Follow the journey",
            emailTitle: "One honest email a month.",
            emailBody: "Real case studies, fresh Karnataka feed prices, and the mistakes we watch first-time farmers make. No sales — this platform doesn't sell cattle.",
            emailLabel: "Your email",
            emailPlaceholder: "you@example.com",
            emailFooter: "Unsubscribe anytime. We never share your email.",
            emailSuccess: "You are on the list.",
            emailSuccessSub: "We will only send you what matters — starting next month.",
            emailInvalid: "Please enter a valid email address.",
            emailSuccessToast: "You are on the list. We will only send you what matters.",
        },
        knowledge: {
            label: "Knowledge Centre",
            heading: "A curriculum for\nthe honest farmer.",
            intro: "Long-form, jargon-free essays on the economics, biology and craft of dairy farming — written for the farmer who wants to invest with clarity.",
            categories: ["All", "Getting Started", "Economics", "Operations", "Infrastructure", "Health"],
        },
        gallery: {
            label: "Gallery",
            heading: "The quiet life\nof Karnataka's dairies.",
            intro: "A visual archive. Documentary-style photography from farms, fields, and the people who care for them.",
            categories: ["All", "Portraits", "Calves", "Landscape"],
        },
        contact: {
            label: "Contact",
            heading: "Visit the farm.\nAsk us anything.",
            intro: "We host visits, answer questions, and help farmers think through their first purchase — with no pressure and no sale.",
            phone: "Phone",
            whatsapp: "WhatsApp",
            email: "Email",
            instagram: "Instagram",
            youtube: "YouTube",
            facebook: "Facebook",
            address: "Address",
            addressBody: "Omkar Dairy Farm\nHN Kaval, Nagamangala Taluk\nMandya District, Karnataka – 571432",
            enquiryLabel: "Farm Visit Enquiry",
            enquiryTitle: "Tell us about your plans.",
            fieldName: "Your name",
            fieldEmail: "Email address",
            fieldPhone: "Phone (optional)",
            fieldMessage: "Your message",
            formError: "Please fill in your name, email and message.",
            formSuccess: "Enquiry received. We will get back to you shortly.",
            directions: "Directions",
            addressShort: "HN Kaval, Nagamangala Taluk, Mandya, Karnataka – 571432",
        },
        footer: {
            visit: "Visit",
            explore: "Explore",
            connect: "Connect",
            tagline: "Learn before you invest.",
            rights: "All rights reserved.",
            crafted: "An educational platform for future dairy farmers.",
        },
    },

    kn: {
        common: {
            langName: "ಕನ್ನಡ",
            langShort: "ಕನ್ನಡ",
            switchTo: "English",
            nav: {
                home: "ಮುಖಪುಟ",
                breeds: "ತಳಿಗಳು",
                knowledge: "ಜ್ಞಾನ ಕೇಂದ್ರ",
                gallery: "ಚಿತ್ರಸಂಗ್ರಹ",
                quiz: "ಪ್ರಶ್ನೆಪಟ್ಟಿ",
                contact: "ಸಂಪರ್ಕ",
                menu: "ಮೆನು",
                close: "ಮುಚ್ಚಿ",
            },
            buttons: {
                explore: "ನೋಡಿ",
                takeQuiz: "60 ಸೆಕೆಂಡ್ ತಳಿ ಪ್ರಶ್ನೆ ಪಡೆಯಿರಿ",
                exploreBreeds: "ತಳಿಗಳನ್ನು ನೋಡಿ",
                learnDairy: "ಹೈನುಗಾರಿಕೆ ಕಲಿಯಿರಿ",
                readEssay: "ಲೇಖನ ಓದಿ",
                viewAll: "ಎಲ್ಲ ತಳಿಗಳನ್ನು ನೋಡಿ",
                sending: "ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…",
                sendEnquiry: "ವಿಚಾರಣೆ ಕಳುಹಿಸಿ",
                begin: "ಪ್ರಶ್ನೆ ಆರಂಭಿಸಿ",
                takeQuizShort: "ಪ್ರಶ್ನೆ ಪಡೆಯಿರಿ",
                retake: "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ",
                previous: "ಹಿಂದಿನ",
                next: "ಮುಂದಿನ",
                openMaps: "ಗೂಗಲ್ ಮ್ಯಾಪ್‌ನಲ್ಲಿ ತೆರೆಯಿರಿ →",
                notSureCta: "ಯಾವುದೆಂದು ಗೊಂದಲವೇ? 60 ಸೆ. ಪ್ರಶ್ನೆ ಮಾಡಿ →",
                readStory: "ಕಥೆ ಓದಿ",
                exploreAll: "ಎಲ್ಲ ತಳಿಗಳನ್ನು ನೋಡಿ",
                sendLetter: "ಪತ್ರ ಕಳುಹಿಸಿ",
            },
            labels: {
                scroll: "ಸ್ಕ್ರೋಲ್",
                match: "ಹೊಂದಾಣಿಕೆ",
                matchScore: "ಹೊಂದಾಣಿಕೆ ಅಂಕ",
                question: "ಪ್ರಶ್ನೆ",
                allBreeds: "ಎಲ್ಲ ತಳಿಗಳು",
                featured: "ವಿಶೇಷ",
                nativeTo: "ಮೂಲ ಸ್ಥಳ",
                readTime: "ನಿಮಿಷ ಓದು",
            },
        },
        floatingQuiz: {
            label: "60ಸೆ ತಳಿ ಪ್ರಶ್ನೆ",
            aria: "ತಳಿ ಪ್ರಶ್ನೆ ಮಾಡಿ",
        },
        home: {
            heroOverline: "ಕರ್ನಾಟಕ · ಸ್ಥಾಪನೆ 2016 · ಶೈಕ್ಷಣಿಕ ವೇದಿಕೆ",
            heroBrand: "ಓಂಕಾರ್\nಹೈನುಗಾರಿಕೆ ಫಾರ್ಮ್",
            heroTagline: "ಹೂಡುವ ಮೊದಲು ಕಲಿಯಿರಿ.",
            heroSub: "ಜಾನುವಾರುಗಳನ್ನು ಖರೀದಿಸುವ ಮೊದಲು — ಹೈನುಗಾರಿಕೆಯ ನಿಜವಾದ ಹೂಡಿಕೆ, ನಿರ್ವಹಣೆ ಮತ್ತು ಆರ್ಥಿಕತೆಯನ್ನು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು ಭವಿಷ್ಯದ ಹೈನುಗಾರರಿಗೆ ಸಹಾಯ.",
            about: {
                label: "ಓಂಕಾರ್ ಬಗ್ಗೆ",
                heading: "ಪ್ರತಿಯೊಂದು ಹಸು\nದೀರ್ಘಕಾಲದ ಹೂಡಿಕೆ.",
                p1: "ಸರಿಯಾದ ತಳಿ ಆಯ್ಕೆ ಎಂದರೆ ಅತಿ ದೊಡ್ಡ ಅಥವಾ ಅತಿ ಹೆಚ್ಚು ಹಾಲು ಕೊಡುವ ಹಸು ಹುಡುಕುವುದಲ್ಲ. ಅದು ನಿಮ್ಮ ಹವಾಮಾನ, ಶ್ರಮ, ಮೇವಿನ ಲಭ್ಯತೆ ಮತ್ತು ಮಾರುಕಟ್ಟೆಗೆ ಹೊಂದುವ ಪ್ರಾಣಿಯನ್ನು ಆಯ್ಕೆ ಮಾಡುವುದು.",
                p2: "ನಮ್ಮ ಉದ್ದೇಶ ಸರಳ — ಕರ್ನಾಟಕಕ್ಕೆ ಸೂಕ್ತವಾದ ಪ್ರತಿಯೊಂದು ತಳಿಯ ಬಗ್ಗೆ ಸ್ಪಷ್ಟ, ಪಾರದರ್ಶಕ, ಪರಿಶೀಲಿಸಬಹುದಾದ ಮಾಹಿತಿಯೊಂದಿಗೆ ಹೂಡುವ ಮೊದಲು ರೈತರಿಗೆ ಶಿಕ್ಷಣ ನೀಡುವುದು.",
                stat1: "ದಾಖಲಿತ ತಳಿಗಳು",
                stat2: "ಕಲಿಕಾ ವಿಷಯಗಳು",
                stat3: "ಮಾರಾಟಕ್ಕೆ ಹಸು",
            },
            breedsSection: {
                label: "ನಮ್ಮ ಹಸು ತಳಿಗಳು",
                heading: "ಆರು ತಳಿಗಳು. ಆರು\nಬೇರೆ ಬೇರೆ ಆರ್ಥಿಕತೆಗಳು.",
            },
            why: {
                label: "ತಳಿ ಆಯ್ಕೆ ಏಕೆ ಮುಖ್ಯ",
                heading: "ತಪ್ಪು ತಳಿ\nಫಾರ್ಮ್‌ಅನ್ನು ಮೌನವಾಗಿ\nದಿವಾಳಿ ಮಾಡಬಹುದು.",
                body: "ಒಂದೇ ರೀತಿಯ ಮೂಲಸೌಕರ್ಯದ ಎರಡು ಫಾರ್ಮ್‌ಗಳಿಗೆ ಸಂಪೂರ್ಣ ಭಿನ್ನ ಆರ್ಥಿಕತೆ ಇರಬಹುದು — ಖರೀದಿ ದಿನ ಕೈಗೊಂಡ ಒಂದೇ ನಿರ್ಧಾರದಿಂದ.",
                items: [
                    "ಹಾಲಿನ ಉತ್ಪಾದನೆ", "ಹಾಲಿನ ಕೊಬ್ಬಿನ ಪ್ರಮಾಣ", "ಆಹಾರ ಅಗತ್ಯತೆಗಳು", "ಆಹಾರದ ವೆಚ್ಚ",
                    "ಶ್ರಮದ ಅಗತ್ಯತೆಗಳು", "ಹವಾಮಾನ ಹೊಂದಾಣಿಕೆ", "ರೋಗ ನಿರೋಧಕತೆ", "ಕಾರ್ಯಾಚರಣಾ ವೆಚ್ಚಗಳು", "ಲಾಭದಾಯಕತೆ",
                ],
            },
            learn: {
                label: "ನೀವು ಏನು ಕಲಿಯುತ್ತೀರಿ",
                heading: "ಖರೀದಿಗೂ ಮುಂಚೆಯೇ\nಸಂಪೂರ್ಣ ಪಠ್ಯಕ್ರಮ.",
            },
            quizCta: {
                label: "60 ಸೆಕೆಂಡ್ ತಳಿ ಪ್ರಶ್ನೆ",
                heading1: "ಯಾವ ಹಸು",
                heading2: "ನಿಮಗೆ",
                heading3: "ಸೂಕ್ತ?",
                body: "ನಿಮ್ಮ ಪ್ರದೇಶ, ಮಾರುಕಟ್ಟೆ, ಬಜೆಟ್, ಶ್ರಮ ಮತ್ತು ಅನುಭವದ ಬಗ್ಗೆ ಐದು ಪ್ರಾಮಾಣಿಕ ಪ್ರಶ್ನೆಗಳು. ಪ್ರತಿ ತಳಿಯನ್ನೂ ನಿಮ್ಮ ವಾಸ್ತವತೆಗೆ ಹೊಂದಾಣಿಸಿ ಸ್ಥಾನ ನೀಡುತ್ತೇವೆ — ಸಂಪೂರ್ಣ ತರ್ಕದೊಂದಿಗೆ.",
                questionsLabel: "ಪ್ರಶ್ನೆಗಳು",
            },
            featured: {
                label: "ವಿಶೇಷ ಕಥೆ",
            },
            begin: {
                label: "ಪ್ರಯಾಣ ಆರಂಭಿಸಿ",
                heading1: "ನಿಮ್ಮ",
                heading2: "ಹೈನುಗಾರಿಕೆ",
                heading3: "ಪ್ರಯಾಣ ಆರಂಭಿಸಲು ಸಿದ್ಧರೇ?",
                body: "ಯಾವುದೇ ಮಾರಾಟದ ಒತ್ತಡವಿಲ್ಲ. ಸ್ಪಷ್ಟ, ಪ್ರಾಮಾಣಿಕ ಮಾಹಿತಿ — ವಿಶ್ವಾಸದೊಂದಿಗೆ ಹೂಡಿಕೆ ಮಾಡಲು.",
            },
        },
        learningTopics: [
            { title: "ದೈನಂದಿನ ಹಾಲಿನ ಉತ್ಪಾದನೆ", body: "ತಳಿ ಮತ್ತು ಋತುಗಳ ಪ್ರಕಾರ ವಾಸ್ತವಿಕ ಹಸು-ಪ್ರತಿ ಉತ್ಪಾದನೆಯನ್ನು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಿ." },
            { title: "ಹಾಲಿನ ಕೊಬ್ಬಿನ ಪ್ರಮಾಣ", body: "ಪರಿಮಾಣಕ್ಕಿಂತ ಕೊಬ್ಬು ಮತ್ತು SNF ಬೆಲೆಯನ್ನು ಏಕೆ ನಿರ್ಧರಿಸುತ್ತವೆ." },
            { title: "ಖರೀದಿ ವೆಚ್ಚ", body: "ಕರು, ಎಳೆ ಹಸು, ಮೊದಲ ಕರುವಿನ ಮತ್ತು ಹಾಲು ಕೊಡುವ ಹಸುಗಳ ಬೆಲೆ ಶ್ರೇಣಿ." },
            { title: "ಆಹಾರದ ಅಗತ್ಯತೆಗಳು", body: "ಹಸಿ ಮೇವು, ಒಣ ಮೇವು, ಸಾಂದ್ರ ಆಹಾರ, ಖನಿಜಗಳು ಮತ್ತು ನೀರು." },
            { title: "ಆಹಾರದ ವೆಚ್ಚ", body: "ಪ್ರತಿ ಪ್ರಾಣಿಗೆ ದೈನಂದಿನ, ಮಾಸಿಕ ಮತ್ತು ವಾರ್ಷಿಕ ಆಹಾರ ವೆಚ್ಚ." },
            { title: "ಶ್ರಮದ ಅಗತ್ಯತೆ", body: "ಆರೋಗ್ಯಕರ ನಿರ್ವಹಣೆಗೆ ಪ್ರತಿ ಹಸು ದಿನಕ್ಕೆ ಬೇಕಾದ ಮಾನವ-ಗಂಟೆಗಳು." },
            { title: "ವಸತಿ", body: "ಆವರಿಸಿದ ಮತ್ತು ತೆರೆದ ಪ್ರದೇಶ, ವಾಯುಪ್ರಸರಣ, ನೆಲ ಮತ್ತು ಒಳಚರಂಡಿ." },
            { title: "ಆರೋಗ್ಯ", body: "ರೋಗ ನಿರೋಧಕತೆ, ಶಾಖದ ಒತ್ತಡ ಮತ್ತು ಪಶು ಚಿಕಿತ್ಸೆ." },
            { title: "ಸಂತಾನೋತ್ಪತ್ತಿ", body: "ಮೊದಲ ಕರುವಿನ ವಯಸ್ಸು, ಗರ್ಭಧಾರಣೆ, ಕರುವಿನ ಅಂತರ ಮತ್ತು AI." },
            { title: "ಲಾಭದಾಯಕತೆ", body: "ಹೂಡಿಕೆ ಮಾಡುವ ಮೊದಲು ಪ್ರಾಮಾಣಿಕ ROI ಲೆಕ್ಕಾಚಾರ ಮಾಡುವುದು." },
            { title: "ROI ಯೋಜನೆ", body: "ಬ್ರೇಕ್-ಈವನ್ ತಿಂಗಳುಗಳು ಮತ್ತು ದೀರ್ಘಾವಧಿಯ ನಗದು ಹರಿವಿನ ಮಾದರಿ." },
        ],
        breedsPage: {
            label: "ಹಸು ತಳಿಗಳು",
            heading: "ಆರು ತಳಿಗಳು\nಆಳವಾಗಿ ದಾಖಲಿಸಲಾಗಿದೆ.",
            intro: "ಕರ್ನಾಟಕಕ್ಕೆ ಪ್ರಸ್ತುತವಾದ ತಳಿಗಳ ಅಧ್ಯಯನಪೂರ್ವಕ, ಮಾಹಿತಿ-ಆಧಾರಿತ ನೋಟ — ಅಧಿಕ ಪ್ರಮಾಣದ ಹಾಲ್‌ಸ್ಟೀನ್ ಫ್ರೀಸಿಯನ್‌ನಿಂದ ಸ್ಥಳೀಯ ಹಳ್ಳಿಕಾರ್‌ವರೆಗೆ. ಪೂರ್ಣ ಆರ್ಥಿಕತೆಗಾಗಿ ಯಾವುದೇ ತಳಿಯ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ.",
            noteLabel: "ಸಂಖ್ಯೆಗಳ ಬಗ್ಗೆ ಟಿಪ್ಪಣಿ",
            noteBody: "ಪ್ರತಿ ತಳಿ ವಿವರದಲ್ಲಿನ ಸಂಖ್ಯೆಗಳು ಸಾಮಾನ್ಯವಾಗಿ ಒಪ್ಪಲಾದ ಹೈನುಗಾರಿಕೆ ನಿರ್ವಹಣಾ ಉಲ್ಲೇಖಗಳಿಂದ ಪಡೆದ ಕರ್ನಾಟಕದ ಸಾಂಪ್ರದಾಯಿಕ ಸರಾಸರಿಗಳಾಗಿವೆ. ವಾಸ್ತವಿಕ ಫಲಿತಾಂಶಗಳು ಆಹಾರ ಗುಣಮಟ್ಟ, ಜೀನ್‌ಗಳು, ಹವಾಮಾನ ಮತ್ತು ನಿರ್ವಹಣೆಯನ್ನು ಅವಲಂಬಿಸಿ ಬದಲಾಗುತ್ತವೆ. ಇಲ್ಲಿ ತೋರಿಸಿದ ಪ್ರತಿ ಮೌಲ್ಯವನ್ನೂ ನಿಮ್ಮ ಸ್ವಂತ ಪರಿಶೀಲಿತ ಫಾರ್ಮ್ ದತ್ತಾಂಶದೊಂದಿಗೆ ಬದಲಾಯಿಸಬಹುದು.",
        },
        breedProfile: {
            sections: {
                origin: "ಮೂಲ",
                production: "ಉತ್ಪಾದನಾ ಸಾಮರ್ಥ್ಯ",
                economics: "ಆರ್ಥಿಕತೆ",
                economicsSub: "ಜೀವನ ಹಂತದ ಪ್ರಕಾರ ಸರಿಸುಮಾರು ಖರೀದಿ ವೆಚ್ಚ",
                feeding: "ಆಹಾರ",
                feedingSub: "ಸಾಮಾನ್ಯ ಕರ್ನಾಟಕದ ಪರಿಸ್ಥಿತಿಯಲ್ಲಿ",
                feedCost: "ದೈನಂದಿನ ಆಹಾರ ವೆಚ್ಚ",
                feedCostSub: "ಉಲ್ಲೇಖ ಬೆಲೆಗಳು — ವಾಸ್ತವಿಕ ವೆಚ್ಚಗಳು ಪ್ರದೇಶ ಮತ್ತು ಋತುವಿಗೆ ಅನುಗುಣವಾಗಿ ಬದಲಾಗುತ್ತವೆ",
                labour: "ಶ್ರಮದ ಅಗತ್ಯತೆ",
                housing: "ವಸತಿ",
                health: "ಆರೋಗ್ಯ",
                reproduction: "ಸಂತಾನೋತ್ಪತ್ತಿ",
                environment: "ಪರಿಸರ ಹೊಂದಾಣಿಕೆ",
                business: "ವ್ಯಾಪಾರ ಸೂಕ್ತತೆ",
                ratings: "ತಜ್ಞರ ರೇಟಿಂಗ್",
                ratingsSub: "0-100 ಪ್ರಮಾಣದಲ್ಲಿ ಸಾಪೇಕ್ಷ ಅಂಕಗಳು",
                recommendation: "ತಜ್ಞರ ಶಿಫಾರಸು",
                gallery: "ಚಿತ್ರಸಂಗ್ರಹ",
            },
            rows: {
                avgYield: "ಸರಾಸರಿ ಹಾಲಿನ ಉತ್ಪಾದನೆ",
                peakYield: "ಗರಿಷ್ಠ ಹಾಲಿನ ಉತ್ಪಾದನೆ",
                lactationPeriod: "ಹಾಲುಣಿಸುವ ಅವಧಿ",
                milkPerLactation: "ಹಾಲುಣಿಸುವಿಕೆಗೆ ಹಾಲಿನ ಪ್ರಮಾಣ",
                milkFat: "ಹಾಲಿನ ಕೊಬ್ಬು",
                snf: "SNF",
                protein: "ಪ್ರೋಟೀನ್",
                suitableFor: "ಸೂಕ್ತ",
                calf: "ಕರು",
                heifer: "ಎಳೆಹಸು",
                firstCalving: "ಮೊದಲ ಕರುವಿನ ಸಮಯ",
                lactatingCow: "ಹಾಲುಕೊಡುವ ಹಸು",
                pregnantCow: "ಗರ್ಭಿಣಿ ಹಸು",
                greenFodder: "ಹಸಿ ಮೇವು",
                dryFodder: "ಒಣ ಮೇವು",
                concentrate: "ಸಾಂದ್ರ ಆಹಾರ",
                mineral: "ಖನಿಜ ಮಿಶ್ರಣ",
                salt: "ಉಪ್ಪು",
                water: "ನೀರು",
                feedType: "ಆಹಾರದ ಪ್ರಕಾರ",
                quantity: "ಪ್ರಮಾಣ",
                daily: "ದೈನಂದಿನ (₹)",
                monthly: "ಮಾಸಿಕ (₹)",
                yearly: "ವಾರ್ಷಿಕ (₹)",
                totalDaily: "ಒಟ್ಟು ದೈನಂದಿನ ಆಹಾರ ವೆಚ್ಚ",
                totalLabour: "ಒಟ್ಟು ಶ್ರಮ",
                labourNote: "ಆರೋಗ್ಯಕರ ಕಾರ್ಯಾಚರಣೆಗಾಗಿ ಪ್ರತಿ ಹಸು ದಿನಕ್ಕೆ ಅಗತ್ಯವಿರುವ ಮಾನವ-ಗಂಟೆಗಳು.",
                coveredArea: "ಆವರಿಸಿದ ಪ್ರದೇಶ",
                openArea: "ತೆರೆದ ಪ್ರದೇಶ",
                ventilation: "ವಾಯುಪ್ರಸರಣ",
                floor: "ನೆಲದ ಪ್ರಕಾರ",
                drainage: "ಒಳಚರಂಡಿ",
                bedding: "ಹಾಸಿಗೆ",
                diseaseResistance: "ರೋಗ ನಿರೋಧಕತೆ",
                heatStress: "ಶಾಖ ಒತ್ತಡ",
                tickResistance: "ಉಣ್ಣಿ ನಿರೋಧಕತೆ",
                mastitis: "ಕೆಚ್ಚಲುಬಾವು ಸಂವೇದನೆ",
                veterinary: "ಪಶು ಚಿಕಿತ್ಸೆ",
                commonDiseases: "ಸಾಮಾನ್ಯ ರೋಗಗಳು",
                firstCalvingAge: "ಮೊದಲ ಕರುವಿನ ವಯಸ್ಸು",
                gestation: "ಗರ್ಭಧಾರಣಾ ಅವಧಿ",
                calvingInterval: "ಕರುವಿನ ಅಂತರ",
                fertility: "ಫಲವತ್ತತೆ",
                insemination: "ಕೃತಕ ಗರ್ಭಧಾರಣೆ",
                natural: "ಸಹಜ ಸಂತಾನೋತ್ಪತ್ತಿ",
                heat: "ಶಾಖ",
                rain: "ಮಳೆ",
                cold: "ಶೀತ",
                humidity: "ಆರ್ದ್ರತೆ",
                bestRegions: "ಕರ್ನಾಟಕದ ಅತ್ಯುತ್ತಮ ಪ್ರದೇಶಗಳು",
                bestSuited: "ಇವರಿಗೆ ಸೂಕ್ತ",
                notRecommended: "ಇವರಿಗೆ ಶಿಫಾರಸು ಮಾಡಲಾಗದು",
                monthlyOp: "ಅಂದಾಜು ಮಾಸಿಕ ಕಾರ್ಯಾಚರಣಾ ವೆಚ್ಚ",
                monthlyMilk: "ನಿರೀಕ್ಷಿತ ಮಾಸಿಕ ಹಾಲಿನ ಉತ್ಪಾದನೆ",
                expectedROI: "ನಿರೀಕ್ಷಿತ ROI",
                advantages: "ಪ್ರಯೋಜನಗಳು",
                challenges: "ಸವಾಲುಗಳು",
                coat: "ಚರ್ಮದ ಬಣ್ಣ",
                classification: "ವರ್ಗೀಕರಣ",
            },
            ratingLabels: {
                milkYield: "ಹಾಲಿನ ಉತ್ಪಾದನೆ",
                milkQuality: "ಹಾಲಿನ ಗುಣಮಟ್ಟ",
                feedEfficiency: "ಆಹಾರ ದಕ್ಷತೆ",
                maintenanceCost: "ನಿರ್ವಹಣಾ ವೆಚ್ಚ",
                diseaseResistance: "ರೋಗ ನಿರೋಧಕತೆ",
                heatTolerance: "ಶಾಖ ಸಹಿಷ್ಣುತೆ",
                labourRequirement: "ಶ್ರಮದ ಅಗತ್ಯತೆ",
                profitability: "ಲಾಭದಾಯಕತೆ",
                beginnerFriendly: "ಆರಂಭಿಕರಿಗೆ ಸೂಕ್ತ",
            },
            tasks: {
                Feeding: "ಆಹಾರ ನೀಡುವಿಕೆ",
                Cleaning: "ಸ್ವಚ್ಛತೆ",
                Milking: "ಹಾಲುಕರೆಯುವಿಕೆ",
                Grooming: "ಆರೈಕೆ",
                "Health Inspection": "ಆರೋಗ್ಯ ಪರಿಶೀಲನೆ",
                "Record Keeping": "ದಾಖಲೆ ನಿರ್ವಹಣೆ",
            },
        },
        quiz: {
            label: "ತಳಿ ಪ್ರಶ್ನೆ",
            introTitle: "ಯಾವ ಹಸು ನಿಮಗೆ ಸೂಕ್ತ?",
            introSub: "ಐದು ಪ್ರಶ್ನೆಗಳು. ಅರವತ್ತು ಸೆಕೆಂಡುಗಳು. ನಿಮ್ಮ ಹವಾಮಾನ, ಮಾರುಕಟ್ಟೆ, ಬಜೆಟ್ ಮತ್ತು ಅನುಭವಕ್ಕೆ ಹೊಂದಾಣಿಸಿದ ತಳಿ — ಪ್ರಾಮಾಣಿಕ ತರ್ಕದೊಂದಿಗೆ.",
            cardsQuestions: "ಪ್ರದೇಶ, ಗುರಿ, ಬಜೆಟ್, ಶ್ರಮ, ಅನುಭವ.",
            cardsTime: "ಒಂದು ಲೇಖನ ಓದುವುದಕ್ಕಿಂತ ವೇಗವಾಗಿ ಪೂರೈಸಿ.",
            cardsScore: "ನಾವು ದಾಖಲಿಸಿದ ಪ್ರತಿ ತಳಿಯಲ್ಲಿ ಶ್ರೇಣಿಕೃತ ಅಂಕ.",
            timeLabel: "ಸಮಯ",
            questionsLabel: "ಪ್ರಶ್ನೆಗಳು",
            scoreLabel: "ತಳಿ ಹೊಂದಾಣಿಕೆ ಅಂಕ",
            resultLabel: "ನಿಮ್ಮ ಹೊಂದಾಣಿಕೆ",
            recommended: "ನಿಮಗೆ ಶಿಫಾರಸು ಮಾಡಲಾಗಿದೆ",
            whySuits: "ಈ ತಳಿ ನಿಮಗೆ ಏಕೆ ಸೂಕ್ತ",
            avgYield: "ಸರಾಸರಿ ಉತ್ಪಾದನೆ",
            milkFat: "ಹಾಲಿನ ಕೊಬ್ಬು",
            monthlyOp: "ಮಾಸಿಕ ವೆಚ್ಚ",
            monthlyMilk: "ಮಾಸಿಕ ಹಾಲು",
            seeProfile: "ಪೂರ್ಣ",
            profileSuffix: "ವಿವರ ನೋಡಿ",
            alsoConsider: "ಇವನ್ನೂ ಪರಿಗಣಿಸಿ",
            followJourney: "ಪ್ರಯಾಣವನ್ನು ಅನುಸರಿಸಿ",
            emailTitle: "ತಿಂಗಳಿಗೊಂದು ಪ್ರಾಮಾಣಿಕ ಇಮೇಲ್.",
            emailBody: "ವಾಸ್ತವಿಕ ಪ್ರಕರಣ ಅಧ್ಯಯನಗಳು, ತಾಜಾ ಕರ್ನಾಟಕ ಆಹಾರ ಬೆಲೆಗಳು ಮತ್ತು ಆರಂಭಿಕ ರೈತರು ಮಾಡುವ ತಪ್ಪುಗಳು. ಮಾರಾಟವಿಲ್ಲ — ಈ ವೇದಿಕೆ ಜಾನುವಾರುಗಳನ್ನು ಮಾರಾಟ ಮಾಡುವುದಿಲ್ಲ.",
            emailLabel: "ನಿಮ್ಮ ಇಮೇಲ್",
            emailPlaceholder: "you@example.com",
            emailFooter: "ಯಾವಾಗ ಬೇಕಾದರೂ ಚಂದಾ ರದ್ದುಗೊಳಿಸಿ. ನಿಮ್ಮ ಇಮೇಲ್ ಹಂಚಿಕೊಳ್ಳುವುದಿಲ್ಲ.",
            emailSuccess: "ನೀವು ಪಟ್ಟಿಯಲ್ಲಿದ್ದೀರಿ.",
            emailSuccessSub: "ಪ್ರಮುಖವಾದವನ್ನೇ ಕಳುಹಿಸುತ್ತೇವೆ — ಮುಂದಿನ ತಿಂಗಳಿನಿಂದ ಆರಂಭ.",
            emailInvalid: "ದಯವಿಟ್ಟು ಸರಿಯಾದ ಇಮೇಲ್ ವಿಳಾಸ ನಮೂದಿಸಿ.",
            emailSuccessToast: "ನೀವು ಪಟ್ಟಿಯಲ್ಲಿದ್ದೀರಿ. ಪ್ರಮುಖವಾದವನ್ನೇ ಕಳುಹಿಸುತ್ತೇವೆ.",
        },
        knowledge: {
            label: "ಜ್ಞಾನ ಕೇಂದ್ರ",
            heading: "ಪ್ರಾಮಾಣಿಕ ರೈತನಿಗಾಗಿ\nಒಂದು ಪಠ್ಯಕ್ರಮ.",
            intro: "ಹೈನುಗಾರಿಕೆಯ ಆರ್ಥಿಕತೆ, ಜೀವಶಾಸ್ತ್ರ ಮತ್ತು ಕಲೆಯ ಬಗ್ಗೆ ದೀರ್ಘ, ಸರಳ ಭಾಷೆಯ ಲೇಖನಗಳು — ಸ್ಪಷ್ಟತೆಯೊಂದಿಗೆ ಹೂಡಿಕೆ ಮಾಡ ಬಯಸುವ ರೈತನಿಗಾಗಿ.",
            categories: ["ಎಲ್ಲಾ", "ಆರಂಭಿಸುವಿಕೆ", "ಆರ್ಥಿಕತೆ", "ಕಾರ್ಯಾಚರಣೆ", "ಮೂಲಸೌಕರ್ಯ", "ಆರೋಗ್ಯ"],
        },
        gallery: {
            label: "ಚಿತ್ರಸಂಗ್ರಹ",
            heading: "ಕರ್ನಾಟಕದ\nಹೈನುಗಾರಿಕೆಯ ಶಾಂತ ಜೀವನ.",
            intro: "ದೃಶ್ಯ ಸಂಗ್ರಹ. ಫಾರ್ಮ್‌ಗಳು, ಹೊಲಗಳು ಮತ್ತು ಅವುಗಳನ್ನು ಆರೈಕೆ ಮಾಡುವ ಜನರ ಸಾಕ್ಷ್ಯಚಿತ್ರ ಶೈಲಿಯ ಛಾಯಾಗ್ರಹಣ.",
            categories: ["ಎಲ್ಲಾ", "ಭಾವಚಿತ್ರ", "ಕರುಗಳು", "ಪ್ರಕೃತಿ"],
        },
        contact: {
            label: "ಸಂಪರ್ಕ",
            heading: "ಫಾರ್ಮ್ ಭೇಟಿ ನೀಡಿ.\nಏನೂ ಕೇಳಿ.",
            intro: "ನಾವು ಭೇಟಿಗಳನ್ನು ಆಯೋಜಿಸುತ್ತೇವೆ, ಪ್ರಶ್ನೆಗಳಿಗೆ ಉತ್ತರಿಸುತ್ತೇವೆ ಮತ್ತು ಮೊದಲ ಖರೀದಿಯ ಮೂಲಕ ಯೋಚಿಸಲು ರೈತರಿಗೆ ಸಹಾಯ ಮಾಡುತ್ತೇವೆ — ಯಾವುದೇ ಒತ್ತಡ ಅಥವಾ ಮಾರಾಟವಿಲ್ಲದೆ.",
            phone: "ದೂರವಾಣಿ",
            whatsapp: "ವಾಟ್ಸ್‌ಆ್ಯಪ್",
            email: "ಇಮೇಲ್",
            instagram: "ಇನ್‌ಸ್ಟಾಗ್ರಾಮ್",
            youtube: "ಯೂಟ್ಯೂಬ್",
            facebook: "ಫೇಸ್‌ಬುಕ್",
            address: "ವಿಳಾಸ",
            addressBody: "ಓಂಕಾರ್ ಡೇರಿ ಫಾರ್ಮ್\nಎಚ್‌ಎನ್ ಕಾವಲ್, ನಾಗಮಂಗಲ ತಾಲೂಕು\nಮಂಡ್ಯ ಜಿಲ್ಲೆ, ಕರ್ನಾಟಕ – 571432",
            enquiryLabel: "ಫಾರ್ಮ್ ಭೇಟಿ ವಿಚಾರಣೆ",
            enquiryTitle: "ನಿಮ್ಮ ಯೋಜನೆಗಳನ್ನು ತಿಳಿಸಿ.",
            fieldName: "ನಿಮ್ಮ ಹೆಸರು",
            fieldEmail: "ಇಮೇಲ್ ವಿಳಾಸ",
            fieldPhone: "ದೂರವಾಣಿ (ಐಚ್ಛಿಕ)",
            fieldMessage: "ನಿಮ್ಮ ಸಂದೇಶ",
            formError: "ದಯವಿಟ್ಟು ಹೆಸರು, ಇಮೇಲ್ ಮತ್ತು ಸಂದೇಶವನ್ನು ಭರ್ತಿ ಮಾಡಿ.",
            formSuccess: "ವಿಚಾರಣೆ ಸ್ವೀಕರಿಸಲಾಗಿದೆ. ಶೀಘ್ರದಲ್ಲಿ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತೇವೆ.",
            directions: "ದಿಕ್ಸೂಚಿಗಳು",
            addressShort: "ಎಚ್‌ಎನ್ ಕಾವಲ್, ನಾಗಮಂಗಲ ತಾಲೂಕು, ಮಂಡ್ಯ, ಕರ್ನಾಟಕ – 571432",
        },
        footer: {
            visit: "ಭೇಟಿ ನೀಡಿ",
            explore: "ನೋಡಿ",
            connect: "ಸಂಪರ್ಕಿಸಿ",
            tagline: "ಹೂಡುವ ಮೊದಲು ಕಲಿಯಿರಿ.",
            rights: "ಎಲ್ಲಾ ಹಕ್ಕುಗಳನ್ನು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ.",
            crafted: "ಭವಿಷ್ಯದ ಹೈನುಗಾರರಿಗಾಗಿ ಶೈಕ್ಷಣಿಕ ವೇದಿಕೆ.",
        },
    },
};
